<template>
    <b-modal :active.sync="localShow" id="endContractModal" aria-modal :width="600" :can-cancel="true">
        <div class="modal-card" style="width: auto">
            <b-loading :active.sync="loading" :is-full-page="false"></b-loading>

            <header class="modal-card-head"><p class="modal-card-title">{{ showAccessMessage ? 'Toegang controleren' : 'Contract beëindigen' }}</p></header>
            <section class="modal-card-body" style="overflow: visible;border-radius:0;">
                <div v-if="showAccessMessage">
                    <p>Voor deze gebruiker is toegang aangemaakt. Wil je de toegang controleren?</p>
                </div>
                <div v-else>
                    <div v-if="contractEndsTodayOrPast" class="notification is-warning">
                        <p class="marginbottom0"><strong>Let op:</strong> {{ contractEndDateWarningText }}<br>
                        Je kunt de einddatum niet opnieuw instellen op dezelfde of een eerdere datum.</p>
                    </div>
                    <div v-else>
                        <p>
                            Bij het aanpassen van de einddatum van het contract ontvangt de klant een e-mail.
                        </p>
                    <p>
                        Gebruik de kalender hieronder om de einddatum te selecteren.<br>
                        Groen gemarkeerde datums zijn factuurmomenten. Geel gemarkeerde datums zijn te beindigen zonder creditfactuur.
                    </p>
                    <p>
                        Wanneer je een eerdere datum kiest, kan er een creditfactuur worden aangemaakt van de resterende dagen binnen de periode van de factuur.
                    </p>

                    <!-- Auto-select first free date instead of direct ending checkbox -->

                    <div v-show="possibleDates">
                        <br>
                        <label><strong>Contract beëindigen na datum</strong></label>

                        <b-field>
                            <b-datepicker
                                placeholder="Klik om te selecteren"
                                :nearby-selectable-month-days="true"
                                icon="calendar-alt"
                                icon-pack="fas"
                                :min-date="moment(new Date()).startOf('day').toDate()"
                                :max-date="maxSelectableDate"
                                v-model="endDateObj"
                                :events="allEventDates"
                                indicators="dots"
                                append-to-body
                            />
                        </b-field>
                    </div>

                    <!-- Show preview for both direct and date-based ending -->
                    <div class="margintop10" v-if="previewLoading">
                        <b-skeleton :animated="true" width="100%" height="22px" />
                        <b-skeleton :animated="true" width="100%" height="22px" />
                    </div>
                    <div class="margintop10" v-else-if="endPreview">
                        <b-notification :type="endPreview.boundaryAligned ? 'is-success is-light' : 'is-info is-light'" :closable="false">
                            <div v-if="endPreview.invoice">
                                <strong>Factuur:</strong> #{{ endPreview.invoice.number }} ({{ moment(endPreview.invoice.invoice_date).format('DD-MM-YYYY') }})
                            </div>
                            <div>
                                <strong>Periode:</strong> {{ moment(endPreview.periodStart).format('DD-MM-YYYY') }} t/m {{ moment(endPreview.periodEnd).format('DD-MM-YYYY') }}
                            </div>
                            <div v-show="endPreview.remainingDays > 0">
                                <strong>Resterende dagen:</strong> {{ endPreview.remainingDays }} van {{ endPreview.daysInPeriod }} – Te crediteren: <strong>€ {{ number_format(endPreview.totalCredit) }}</strong>
                            </div>
                            <div v-if="endPreview.boundaryAligned" class="margintop5">
                                <hr>
                                Geen resterende dagen in deze periode. Contract kan veilig beëindigd worden zonder creditfactuur.
                            </div>
                        </b-notification>
                    </div>

                    <br>

                    <div v-if="!contractEndsTodayOrPast">
                        <label><strong>Facturen annuleren</strong></label><br><br>
                        <b-notification v-if="incassoWarning" type="is-danger is-light" :closable="false" class="margintop5">
                            {{ incassoWarning }}
                        </b-notification>
                        <b-checkbox v-model="cancelInvoices" :disabled="unpaidInvoicesFromDate === 0 || previewLoading">Annuleer alle niet betaalde facturen vanaf <span>{{ fromDateString ? moment(fromDateString).format('DD-MM-YYYY') : moment(endDateObj).format('DD-MM-YYYY') }}</span> ({{ unpaidInvoicesFromDate }} gevonden).</b-checkbox>
                        <b-checkbox v-model="cancelAllInvoices" :disabled="unpaidInvoices === 0 || previewLoading">
                            <span class="text-light">Annuleer alle niet betaalde facturen ({{ unpaidInvoices }} gevonden).</span>
                        </b-checkbox>
                    </div>
                    </div>
                </div>
            </section>
            <footer class="modal-card-foot text-right">
                <button class="button" type="button" @click="$emit('closeEndContractModal')">Annuleren</button>
                <router-link class="button is-success" v-if="showAccessMessage" type="button" :to="{ name: 'ConnectRecord', params: {id: hasAccess} }">Toegang controleren</router-link>
                <button v-else class="button is-success" @click="endContract" :disabled="!endDateObj || previewLoading || contractEndsTodayOrPast">Bevestigen</button>
            </footer>
        </div>
    </b-modal>
</template>

<script>
import toastMixins from '@/mixins/toastMixins.js'
import endingContractMixin from '@/mixins/endingContractMixin.js'

export default {
    name: 'ContractsRecordEndContractModal',

    mixins: [toastMixins, endingContractMixin],

    props: {
        show: {
            type: Boolean,
            required: true,
            default: false
        },
        record: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            localShow: false,
            loading: false,
            endDateObj: moment(new Date()).startOf('day').toDate(),
            cancelInvoices: true,
            cancelAllInvoices: false,
            possibleDates: [],

            unpaidInvoices: 0,
            unpaidInvoicesFromDate: 0,
            fromDateString: null,

            hasAccess: false,
            showAccessMessage: false,
            previewLoading: false,
            endPreview: null,
            forcedDateIsTodayOrPast: false,
            incassoWarning: null
        }
    },

    computed: {
        invoiceType() {
            return moment(new Date()).format('D') < 15 ? 'pre15' : 'after15'
        },
        invoiceEventDates(){
            // Green dots for invoice dates
            return this.possibleDates.map(d => moment(d).toDate())
        },
        freeDayEvents(){
            // Yellow dots for "free" days (actual invoice date, since ending on invoice date = no credit)
            return this.possibleDates.map(d => moment(d).toDate())
        },
        allEventDates(){
            // For weekly contracts: Yellow = end of periods (free), Green = start of periods (next invoice)
            // For monthly contracts: Yellow = invoice dates (free), Green = day after
            const frequency = this.record?.step1?.invoice_freq
            const isWeekly = typeof frequency === 'string' && frequency.indexOf('week_') === 0

            if (isWeekly) {
                const parts = frequency.split('_')
                const weeksPerInvoice = parts.length > 1 ? parseInt(parts[1]) : 1
                const safeWeeksPerInvoice = isNaN(weeksPerInvoice) ? 1 : weeksPerInvoice
                const contractStartDate = moment(this.record.step1.start_date)
                const today = moment().startOf('day')

                // For weekly: Green dots on invoice dates (period starts), Yellow dots on period ends (free days)
                const invoiceDates = this.possibleDates.map(d => ({date: moment(d).toDate(), type: 'is-success'}))
                const freeDays = this.possibleDates.map(d => {
                    // Period end is invoiceDate + (weeks * 7 - 1) days
                    const periodEnd = moment(d).add((safeWeeksPerInvoice * 7) - 1, 'days').toDate()
                    return {date: periodEnd, type: 'is-warning'}
                })

                // Also add the current period's end date if we're in the middle of a period
                let currentInvoiceDate = contractStartDate.clone()
                while (currentInvoiceDate.isBefore(today, 'day')) {
                    currentInvoiceDate = currentInvoiceDate.clone().add(safeWeeksPerInvoice * 7, 'days')
                }
                // Go back one period to get the current period start
                const currentPeriodStart = currentInvoiceDate.clone().subtract(safeWeeksPerInvoice * 7, 'days')
                if (currentPeriodStart.isSameOrBefore(today, 'day')) {
                    const currentPeriodEnd = currentPeriodStart.clone().add((safeWeeksPerInvoice * 7) - 1, 'days')
                    if (currentPeriodEnd.isAfter(today, 'day')) {
                        // We're in the middle of this period, add its end as a free day
                        freeDays.push({date: currentPeriodEnd.toDate(), type: 'is-warning'})
                    }
                }

                return [...invoiceDates, ...freeDays]
            } else {
                // For monthly: Yellow dots on invoice dates (free), Green dots day after
                const invoiceDates = this.possibleDates.map(d => {
                    const nextDay = moment(d).add(1, 'day').toDate()
                    return {date: nextDay, type: 'is-success'}
                })
                const freeDays = this.possibleDates.map(d => ({date: moment(d).toDate(), type: 'is-warning'}))
                return [...invoiceDates, ...freeDays]
            }
        },
        endDateString(){
            return moment(this.endDateObj).format('YYYY-MM-DD')
        },

        maxSelectableDate() {
            if (!this.record?.step1?.end_date) return null
            // Users cannot select a date later than the contract's current end_date (e.g. stale tab)
            return moment(this.record.step1.end_date).startOf('day').toDate()
        },

        contractEndsTodayOrPast() {
            if (this.forcedDateIsTodayOrPast) return true
            if (!this.record?.step1?.end_date) return false
            const today = moment().startOf('day')
            const contractEndDate = moment(this.record.step1.end_date).startOf('day')
            return contractEndDate.isSameOrBefore(today)
        },
        contractEndDateWarningText() {
            const fromRecord = this.record?.step1?.end_date && moment(this.record.step1.end_date).isValid()
            if (fromRecord) return `Dit contract eindigt op ${moment(this.record.step1.end_date).format('DD-MM-YYYY')}.`
            const fromPicker = this.endDateObj && moment(this.endDateObj).isValid()
            if (fromPicker) return `Dit contract eindigt op ${moment(this.endDateObj).format('DD-MM-YYYY')}.`
            return 'Dit contract is reeds beëindigd.'
        }
    },

    watch: {
        show: {
            immediate: true,
            handler(newVal) {
                this.localShow = newVal
                if (newVal) {
                    // Reset checkboxes and forced-date flag
                    this.cancelInvoices = true
                    this.cancelAllInvoices = false
                    this.forcedDateIsTodayOrPast = false

                    // Only initialize if we have proper record data
                    if (this.record && this.record.step1 && this.record.step1.invoice_freq && this.record.step1.start_date) {
                        this.setDates()
                    } else {
                        console.log('Waiting for record data to load before initializing modal...')
                    }
                }
            }
        },
        localShow(newVal) {
            if (!newVal) {
                this.$emit('closeEndContractModal')
            }
        },
        'record.step1': {
            handler(newVal) {
                if (newVal && newVal.invoice_freq && newVal.start_date && this.localShow) {
                    this.setDates()
                }
            },
            deep: true
        },
        cancelInvoices(newVal) {
            if(newVal) {
                this.cancelAllInvoices = false
            }
        },
        cancelAllInvoices(newVal) {
            if(newVal) {
                this.cancelInvoices = false
            }
        },
        endDateObj(newVal) {
            if(!_.isNull(newVal)){
                this.fetchEndPreview() // This now includes unpaid invoices data
            }
        }
    },

    methods: {
        number_format(val, decimals = 2) {
            val = parseFloat(val)
            return isNaN(val) ? '0.00' : val.toFixed(decimals)
        },

        fetchEndPreview() {
            if (!this.endDateString) {
                console.log('No end date string available for preview')
                return
            }

            // Also ensure we have the contract ID
            if (!this.$route.params.id) {
                console.log('No contract ID available for preview')
                return
            }

            this.previewLoading = true
            this.incassoWarning = null

            axios.get(`/api/contract/${this.$route.params.id}/end-preview?date=${this.endDateString}`, {
                headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
            }).then((resp) => {
                this.endPreview = resp.data
                // Set unpaid invoices data from preview response
                if (resp.data.unpaidInvoices) {
                    this.unpaidInvoices = resp.data.unpaidInvoices.total
                    this.unpaidInvoicesFromDate = resp.data.unpaidInvoices.fromDate
                    this.hasAccess = resp.data.unpaidInvoices.access
                    this.fromDateString = resp.data.unpaidInvoices.fromDateString

                    if (resp.data.unpaidInvoices.fromDate == 0) {
                        this.cancelInvoices = false
                    } else {
                        // Auto-select cancelInvoices if there are unpaid invoices from the selected date
                        this.cancelInvoices = true
                        this.cancelAllInvoices = false
                    }

                    // If preview indicates that at least one invoice from the cancellable set
                    // (fromDate-based cancellation) OR the current invoice in the selected period
                    // already has an incasso sent to the bank, show a warning immediately.
                    // Only when future invoices are affected (blockedFromDate) do we also disable
                    // the cancellation checkboxes.
                    const blockedFromDate = resp.data.unpaidInvoices.blockedFromDate || []
                    const currentInvoiceBlocked = resp.data.unpaidInvoices.currentInvoiceBlocked || null

                    const allBlocked = currentInvoiceBlocked
                        ? [...blockedFromDate, currentInvoiceBlocked]
                        : blockedFromDate

                    if (allBlocked.length > 0) {
                        let message = 'Er is al een incasso naar de bank verstuurd voor een van de gevonden facturen. De incasso kan niet meer worden tegengehouden via deze actie. Als je facturen annuleren hebt geselecteerd, wordt de betreffende factuur niet geannuleerd door deze actie. Maak na ontvangst van de betaling een creditfactuur om dit te corrigeren.'

                        const uniqueNumbers = _.uniq(
                            allBlocked
                                .map(inv => inv.number)
                                .filter(n => !!n)
                        )

                        if (uniqueNumbers.length > 0) {
                            message += ` (Factuurnummer(s): ${uniqueNumbers.join(', ')}).`
                        }

                        this.incassoWarning = message

                        // Only disable cancellation options when future invoices in the
                        // cancellable range are blocked; for the current invoice we just warn.
                        if (blockedFromDate.length > 0) {
                            this.cancelInvoices = false
                            this.cancelAllInvoices = false
                        }
                    } else {
                        this.incassoWarning = null
                    }
                }
            }).catch((err) => {
                this.endPreview = null
                // If backend says chosen date is after contract end (e.g. stale tab), force date to contract end
                if (err.response?.data?.error === 'date-after-end' && err.response?.data?.maxEndDate) {
                    const maxEnd = moment(err.response.data.maxEndDate).startOf('day')
                    this.endDateObj = maxEnd.isValid() ? maxEnd.toDate() : this.endDateObj
                    const today = moment().startOf('day')

                    if (maxEnd.isValid() && maxEnd.isSameOrBefore(today)) {
                        this.forcedDateIsTodayOrPast = true
                        this.showMessage(`Let op: Dit contract eindigt op ${maxEnd.format('DD-MM-YYYY')}. Je kunt de einddatum niet opnieuw instellen op dezelfde of een eerdere datum.`, 'warning')
                    } else if (maxEnd.isValid()) {
                        this.showMessage('De gekozen datum was later dan de contracteinddatum. Datum is aangepast.', 'warning')
                    } else {
                        this.showMessage('Let op: Dit contract is reeds beëindigd. Je kunt de einddatum niet opnieuw instellen.', 'warning')
                    }
                }
            }).then(() => {
                this.loading = false
                this.previewLoading = false
            })
        },
        setDates(){
            this.loading = true
            this.previewLoading = true

            // Ensure we have the required record data before proceeding
            if (!this.record || !this.record.step1 || !this.record.step1.invoice_freq || !this.record.step1.start_date) {
                console.log('Missing required record data for setDates:', {
                    hasRecord: !!this.record,
                    hasStep1: !!this.record?.step1,
                    hasFreq: !!this.record?.step1?.invoice_freq,
                    hasStartDate: !!this.record?.step1?.start_date
                })
                return
            }

            // Create possible dates
            let dates = []
            const frequency = this.record.step1.invoice_freq
            const today = moment().startOf('day')
            const contractStartDate = moment(this.record.step1.start_date)

                        // Weekly variants: week_1..week_5
            if (typeof frequency === 'string' && frequency.indexOf('week_') === 0) {
                const parts = frequency.split('_')
                const weeksPerInvoice = parts.length > 1 ? parseInt(parts[1]) : 1
                const safeWeeksPerInvoice = isNaN(weeksPerInvoice) ? 1 : weeksPerInvoice

                // Match backend logic: align with actual invoice dates
                // For weekly contracts, invoice dates are: start_date + (n * weeksPerInvoice * 7) days
                let currentInvoiceDate = contractStartDate.clone()

                // Find the first invoice date that's today or later
                while (currentInvoiceDate.isBefore(today, 'day')) {
                    currentInvoiceDate = currentInvoiceDate.clone().add(safeWeeksPerInvoice * 7, 'days')
                }

                // Generate next 24 invoice dates based on actual weekly intervals
                for (let i = 0; i < 24; i++) {
                    let invoiceDate = currentInvoiceDate.clone().add(i * safeWeeksPerInvoice * 7, 'days')

                    if (!_.isNull(this.record.step1.end_date)) {
                        if (invoiceDate.format('YYYYMMDD') < moment(this.record.step1.end_date).format('YYYYMMDD')) {
                            dates.push(invoiceDate.format('YYYY-MM-DD'))
                        }
                    } else {
                        dates.push(invoiceDate.format('YYYY-MM-DD'))
                    }
                }
            } else {
                const monthsToAdd = frequency === 'quarter' ? 3 : frequency === 'halfyear' ? 6 : frequency === 'year' ? 12 : 1

                // Only use contract start date if it's in current month
                const isStartDateCurrentMonth = contractStartDate.isSame(today, 'month')

                // Determine if we should use pre15 or after15 logic
                let isPre15
                if (isStartDateCurrentMonth) {
                    isPre15 = contractStartDate.date() < 15
                } else {
                    isPre15 = today.date() < 15
                }

                // Set base date to today
                let baseDate = today.clone()

                // For pre15: First period ends at end of current month
                // For after15: First period ends at end of next month
                if (isPre15) {
                    baseDate = baseDate.endOf('month')
                } else {
                    baseDate = baseDate.add(1, 'month').endOf('month')
                }

                // Generate next 12 periods based on frequency
                for (let i = 0; i < 12; i++) {
                    // Calculate the date by adding months and then set it to the end of that month
                    let date = baseDate.clone().add(i * monthsToAdd, 'months').endOf('month');

                    // If end_date is set, only include dates before it
                    if (!_.isNull(this.record.step1.end_date)) {
                        if (date.format('YYYYMMDD') < moment(this.record.step1.end_date).format('YYYYMMDD')) {
                            dates.push(date.format('YYYY-MM-DD'))
                        }
                    } else {
                        dates.push(date.format('YYYY-MM-DD'))
                    }
                }
            }

            this.possibleDates = dates

            // Auto-select first free date (no credit needed)
            if(this.possibleDates.length > 0){
                this.endDateObj = moment(this.possibleDates[0]).toDate()
            }

            // Set default end date (if set and found in coming periods)
            if(!_.isNull(this.record.step1.end_date)){
                const indexOfEndDate = dates.indexOf(moment(this.record.step1.end_date).format('YYYY-MM-DD'))
                if(indexOfEndDate !== -1){
                    this.endDateObj = moment(this.record.step1.end_date).toDate()
                }
            }

            this.fetchEndPreview() // This now includes unpaid invoices data
        },

        // Removed getUnpaidInvoices - now included in fetchEndPreview

        endContract(){
            this.loading = true

            axios.post('/api/contract/'+this.$route.params.id+'/end-contract',
            {
                endDate: this.endDateString,
                cancelInvoices: this.cancelInvoices,
                cancelAllInvoices: this.cancelAllInvoices,
            }, {
                headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
            }).then((response) => {
                this.processContractEndingResponse(response, 'Contract einddatum aangepast!')
            }).catch((error) => {
                console.error(error)

                if(_.has(error, 'response.data.error') && error.response.data.error == 'not-org-admin'){
                    this.showError('Opslaan niet mogelijk, enkel een administrator is gemachtigd om deze gegevens aan te passen.')
                    this.closeEndContractModal()
                } else {
                    this.showError('Daar gaat iets fout! Probeer het opnieuw!')
                    this.closeEndContractModal()
                }
            }).then(() => {
                setTimeout(() => this.loading = false, 500)
            })
        }
    }
}
</script>

<style scoped>
.text-light {
    color: #969696;
}
</style>
